import { useCallback, useMemo } from 'react';
import isEqual from 'lodash/isEqual';
import uniq from 'lodash/uniq';
import memoizeOne from 'memoize-one';
import { useIssueTypesAndFields } from '@atlassian/jira-business-entity-project/src/services/issue-types-and-fields/index.tsx';
import type { BusinessIssueField } from '@atlassian/jira-business-entity-project/src/services/issue-types-and-fields/types.tsx';
import { useHierarchyCache } from '@atlassian/jira-business-list-handlers/src/controllers/hierarchy-cache/index.tsx';
import type { Field } from '@atlassian/jira-business-preferences/src/types.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { DEV_SUMMARY_CF_TYPE } from '@atlassian/jira-platform-field-config/src/index.tsx';
import { useConstants, DEFAULT_CUSTOM_FIELDS_BY_TYPE } from './constants/index.tsx';
import { useFieldsActions, useUpdateFieldsDetail } from './fields-actions/index.tsx';
import type { GetIssueFieldsConstants, UseIssueFieldsReturn } from './types.tsx';
import { useFieldsCache } from './utils/use-fields-cache.tsx';

const fieldIdsToQuery = ['rank', 'parent'];

const buildListOfAvailableFields = memoizeOne(
	(fields: BusinessIssueField[], constants: GetIssueFieldsConstants): BusinessIssueField[] =>
		fields
			// add fields missing in the service response
			.concat(constants.MISSING_FIELDS)
			// filter out fields that we can't render yet
			.filter((field) => constants.SUPPORTED_FIELD_TYPES.includes(field.type ?? ''))
			// sort in order of SUPPORTED_FIELDS
			.sort(
				(a, b) =>
					constants.SUPPORTED_FIELD_TYPES.indexOf(a.type ?? '') -
					constants.SUPPORTED_FIELD_TYPES.indexOf(b.type ?? ''),
			),
	isEqual,
);

const getSortedAvailableFields = memoizeOne(
	(
		availableFields: BusinessIssueField[],
		fieldsOrder: string[],
		constants: GetIssueFieldsConstants,
	): BusinessIssueField[] =>
		availableFields.sort((a, b) => {
			// fieldsOrder are initially set to SUPPORTED_FIELD_TYPES which are typed base
			// Category field is a custom field and it's id (e.g, customfield_X) is not equal to the type
			// so finding by id will return -1
			const aFieldsOrderIdx =
				a?.type && DEFAULT_CUSTOM_FIELDS_BY_TYPE.includes(a?.type)
					? fieldsOrder.indexOf(a.type)
					: fieldsOrder.indexOf(a.id);
			const bFieldsOrderIdx =
				b?.type && DEFAULT_CUSTOM_FIELDS_BY_TYPE.includes(b?.type)
					? fieldsOrder.indexOf(b.type)
					: fieldsOrder.indexOf(b.id);

			// if field is missing from fieldsOrder add to the end sorted based on field type
			if (aFieldsOrderIdx === -1 || bFieldsOrderIdx === -1) {
				if (aFieldsOrderIdx !== -1) {
					return -1;
				}
				if (bFieldsOrderIdx !== -1) {
					return 1;
				}
				return (
					constants.SUPPORTED_FIELD_TYPES.indexOf(a.type ?? '') -
					constants.SUPPORTED_FIELD_TYPES.indexOf(b.type ?? '')
				);
			}

			return aFieldsOrderIdx - bFieldsOrderIdx;
		}),
);

export const useIssueFields = (): UseIssueFieldsReturn => {
	const constants = useConstants();

	const { data, loading, error } = useIssueTypesAndFields({
		issueOperation: 'VIEW',
	});

	const availableFields = buildListOfAvailableFields(data.fields, constants);

	const {
		fields: { order, details },
		toggleField,
		reorderFields,
		resetCache,
	} = useFieldsActions(!loading && error == null ? availableFields : null);

	const getAvailableFields = useCallback((): BusinessIssueField[] => {
		try {
			return getSortedAvailableFields(availableFields, order, constants);
		} catch (err) {
			resetCache && resetCache();
			fireErrorAnalytics({
				meta: {
					id: 'getAvailableFields',
					packageName: 'jiraBusinessList',
					teamName: 'keystone',
				},
				attributes: { message: 'Error trying to get available fields from cache' },
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				error: err as Error,
				sendToPrivacyUnsafeSplunk: true,
			});
			return getSortedAvailableFields(availableFields, [], constants);
		}
	}, [availableFields, constants, order, resetCache]);

	const shouldGetVisibleFieldsFromCache = useMemo(
		() => (loading || error != null) && details != null,
		[details, error, loading],
	);

	const getVisibleFields = useCallback((): BusinessIssueField[] | Field[] => {
		if (shouldGetVisibleFieldsFromCache) {
			try {
				return order
					.map((id) => details?.[id])
					.filter((field: Field | undefined): field is Field =>
						constants.SUPPORTED_FIELD_TYPES.includes(field?.type ?? ''),
					);
			} catch (err) {
				resetCache && resetCache();
				fireErrorAnalytics({
					meta: {
						id: 'getVisibleFields',
						packageName: 'jiraBusinessList',
						teamName: 'keystone',
					},
					attributes: { message: 'Error trying to get visible fields from cache' },
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					error: err as Error,
					sendToPrivacyUnsafeSplunk: true,
				});
				return [];
			}
		}

		return getAvailableFields().filter(
			(field) =>
				constants.ALWAYS_VISIBLE_FIELDS.includes(field.id) ||
				(field.id != null && order.includes(field.id)) ||
				(field.type !== undefined && order.includes(field.type)),
		);
	}, [
		shouldGetVisibleFieldsFromCache,
		getAvailableFields,
		order,
		details,
		constants.SUPPORTED_FIELD_TYPES,
		constants.ALWAYS_VISIBLE_FIELDS,
		resetCache,
	]);

	const getQueryFieldIds = useCallback(() => {
		if (loading) {
			return [];
		}
		const visibleFieldIds = getVisibleFields()
			// the top level dev summary is the only thing that we should be using
			// we need to remove it from fieldIds because could fetch the field twice and is a complex field
			.filter((field) => field.type !== DEV_SUMMARY_CF_TYPE)
			.map((field) => (field.id === 'key' ? 'issuekey' : field.id))
			// avoid querying items on columns re-ordering
			.sort(
				(a, b) =>
					constants.SUPPORTED_FIELD_TYPES.indexOf(a) - constants.SUPPORTED_FIELD_TYPES.indexOf(b),
			)
			.concat(fieldIdsToQuery);

		if (
			visibleFieldIds.includes('timeoriginalestimate') &&
			!visibleFieldIds.includes('timetracking')
		) {
			// In JIS2, replace timeoriginalestimate with timetracking, which will have the information we
			// need to determine timeAsString in the JIS2 transformer in
			// src/packages/business/list/src/services/get-list-view-issues/field-transformer-jis2/index.tsx
			visibleFieldIds.splice(visibleFieldIds.indexOf('timeoriginalestimate'), 1, 'timetracking');
		}
		const queryFieldIds = visibleFieldIds.filter((field) => field !== 'commentsummary');

		return uniq(queryFieldIds);
	}, [constants.SUPPORTED_FIELD_TYPES, getVisibleFields, loading]);

	const { getQueryFieldIdsWithCache } = useFieldsCache({
		loading,
		error,
		getQueryFieldIds,
	});

	const { hierarchyLevelsWithCache } = useHierarchyCache({
		view: 'list',
	});

	const isParentFieldVisible = useCallback(
		() => getVisibleFields().some((field) => field.id === 'parent'),
		[getVisibleFields],
	);

	const isDevSummaryFieldVisible = useCallback(
		() => getVisibleFields().some((field) => field.type === DEV_SUMMARY_CF_TYPE),
		[getVisibleFields],
	);

	return {
		constants,
		toggleField,
		getVisibleFields,
		getAvailableFields,
		getQueryFieldIds: getQueryFieldIdsWithCache,
		isParentFieldVisible,
		isDevSummaryFieldVisible,
		hierarchyLevelsWithCache,
		reorderFields,
		fieldsOrder: order,
		isVisibleFieldsCached: shouldGetVisibleFieldsFromCache,
		loading,
		error,
	};
};

export const UpdateFieldsDetail = () => {
	const constants = useConstants();

	const { data, loading, error } = useIssueTypesAndFields({
		issueOperation: 'VIEW',
	});

	const availableFields = buildListOfAvailableFields(data.fields, constants);

	useUpdateFieldsDetail(!loading && error == null ? availableFields : null);

	return null;
};
